import { useCallback, useContext, useMemo, useState } from "react";
import { NowContext } from "./nowContext";
import { getNowMaybeSimulated } from "../utils/time";

/**
 * Provides a stopwatch that can be started and stopped.
 * Updates the elapsed time every seconds.
 * To increase accuracy, the elapsed time is calculated as a difference between the current time and the time when the stopwatch was last started/un-paused.
 * Elapsed time before the last pause is accumulated as a separate value (and added to this difference before returning the elapsed time).
 */
const useStopwatch = () => {
    const { timestampSeconds } = useContext(NowContext);

    const [accumulatedElapsed, setAccumulatedElapsed] = useState(0);
    const [lastStartTime, setLastStartTime] = useState<number | null>(null);

    const start = useCallback((setToElapsedMillis?: number) => {
        const newStartTime = getNowMaybeSimulated();
        if (setToElapsedMillis !== undefined) {
            //force elapsed time to be set to a specific value and then start with new start time
            console.debug("⏱️ start  (" + newStartTime + "):                   set to " + setToElapsedMillis + "ms");
            setAccumulatedElapsed(setToElapsedMillis);
            setLastStartTime(newStartTime);
        } else {
            console.debug("⏱️ start  (" + newStartTime + ")");
            //set lastStartTime to newStartTime - only if it was null before (i.e. if the stopwatch was not running)
            //this will cause "isRunning = true" in the next render
            setLastStartTime((old) => old === null ? newStartTime : old);
        }
    }, []);

    const stop = useCallback((setToElapsedMillis?: number) => {
        if (lastStartTime !== null) {
            const now = getNowMaybeSimulated();
            const exactCurrentRunningTime = now - lastStartTime;
            //remember current running time in state
            if (setToElapsedMillis !== undefined) {
                console.debug("⏱️   stop (" + now + "):                   set to " + setToElapsedMillis + "ms");
                setAccumulatedElapsed(setToElapsedMillis);
            } else {
                console.debug("⏱️   stop (" + now + "): running " + exactCurrentRunningTime + "ms");
                setAccumulatedElapsed(old => old + exactCurrentRunningTime);
            }
            //stop the watch
            setLastStartTime(null);
        }
    }, [lastStartTime]);

    const reset = useCallback(() => {
        setAccumulatedElapsed(0);
        setLastStartTime(null);
    }, []);


    const getElapsedMillis = useCallback(() => {
        if (lastStartTime === null) return accumulatedElapsed;
        const now = getNowMaybeSimulated();
        const exactCurrentRunningTime = now - lastStartTime;
        return accumulatedElapsed + exactCurrentRunningTime
    }, [accumulatedElapsed, lastStartTime]);

    const stopWatch = useMemo(() => {
        let coarseRunningTime = lastStartTime === null ? 0 : timestampSeconds * 1000 - lastStartTime;
        if (coarseRunningTime < 0) coarseRunningTime = 0;
        const elapsedMillis = accumulatedElapsed + coarseRunningTime;
        const elapsedSeconds = Math.floor(elapsedMillis / 1000);
        const isRunning = lastStartTime !== null;
        // console.debug("⏱️ render (" + Date.now() + "): " +
        //     (isRunning ? "running " : "paused  ") +
        //     coarseRunningTime + "ms + accumulated " + accumulatedElapsed + "ms = " +
        //     elapsedMillis + "ms = " + elapsedSeconds + "s");

        return {
            isRunning,
            getElapsedMillis,
            elapsedSeconds,
            start,
            stop,
            reset
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accumulatedElapsed, lastStartTime, timestampSeconds]);

    return stopWatch;
}

export default useStopwatch;